import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext'
import { useChat } from '@/contexts/ChatContext'

interface MessageStatus {
  status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED'
  delivered_at?: string | null
  read_at?: string | null
  error_message?: string | null
  updated_at?: string
}

interface MessageStatuses {
  byMessageId: Record<string, MessageStatus>
  byWhatsappMessageId: Record<string, MessageStatus>
}

interface MessageStatusContextType {
  getMessageStatus: (messageId: string, whatsappMessageId?: string | null) => MessageStatus | null
  updateMessageStatus: (messageId: string, status: MessageStatus, whatsappMessageId?: string | null) => void
  clearStatuses: () => void
}

const MessageStatusContext = createContext<MessageStatusContextType | undefined>(undefined)

export function MessageStatusProvider({ children }: { children: React.ReactNode }) {
  const businessAccountContext = useBusinessAccountContext()
  const chatContext = useChat()
  const [statuses, setStatuses] = useState<MessageStatuses>({
    byMessageId: {},
    byWhatsappMessageId: {}
  })
  const processedEvents = useRef<Set<string>>(new Set())

  // If BusinessAccountProvider or ChatProvider is not available, return default values
  if (!businessAccountContext || !chatContext) {
    const defaultContext: MessageStatusContextType = {
      getMessageStatus: () => null,
      updateMessageStatus: () => {},
      clearStatuses: () => {}
    }
    return (
      <MessageStatusContext.Provider value={defaultContext}>
        {children}
      </MessageStatusContext.Provider>
    )
  }

  const { socket, isConnected } = chatContext

  // Setup Socket.IO listeners for real-time message status updates (GLOBAL - always active)
  // CRITICAL: This listener attaches ONCE at app start and NEVER disconnects
  // It does NOT depend on rooms - it receives events from the global business account channel
  useEffect(() => {
    if (!socket) {
      return
    }

    // Handle message status updates
    const handleMessageStatusUpdate = (data: any) => {
      const conversationId = data.conversationId || data.conversation_id
      const messageId = data.messageId || data.message_id
      const whatsappMessageId = data.whatsappMessageId || data.whatsapp_message_id
      const status = (data.status || '').toUpperCase() as MessageStatus['status']

      if (!messageId && !whatsappMessageId) {
        return
      }

      if (!status || !['SENT', 'DELIVERED', 'READ', 'FAILED'].includes(status)) {
        return
      }

      // Create event key for deduplication
      const eventKey = `${status}-${conversationId}-${messageId || whatsappMessageId}-${data.timestamp || Date.now()}`
      if (processedEvents.current.has(eventKey)) {
        return
      }
      processedEvents.current.add(eventKey)

      // Failure reason from the webhook (only present on FAILED updates). Kept
      // so the inbox "i" tooltip shows WHY a message failed in real time.
      const incomingError = data.error_message || data.errorMessage || null

      // Update status in global state
      setStatuses(prev => {
        const newStatus: MessageStatus = {
          status,
          delivered_at: status === 'DELIVERED'
            ? (data.timestamp || data.deliveredAt || data.delivered_at || new Date().toISOString())
            : prev.byMessageId[messageId]?.delivered_at || prev.byWhatsappMessageId[whatsappMessageId || '']?.delivered_at || null,
          read_at: status === 'READ'
            ? (data.timestamp || data.readAt || data.read_at || new Date().toISOString())
            : prev.byMessageId[messageId]?.read_at || prev.byWhatsappMessageId[whatsappMessageId || '']?.read_at || null,
          error_message: status === 'FAILED'
            ? (incomingError || prev.byMessageId[messageId]?.error_message || prev.byWhatsappMessageId[whatsappMessageId || '']?.error_message || null)
            : null,
          updated_at: data.timestamp || new Date().toISOString()
        }

        const updated = { ...prev }
        
        if (messageId) {
          updated.byMessageId[messageId] = {
            ...updated.byMessageId[messageId],
            ...newStatus
          }
        }

        if (whatsappMessageId) {
          updated.byWhatsappMessageId[whatsappMessageId] = {
            ...updated.byWhatsappMessageId[whatsappMessageId],
            ...newStatus
          }
        }

        return updated
      })

      // Clean up old events after 5 seconds
      setTimeout(() => processedEvents.current.delete(eventKey), 5000)
    }

    // Attach listener - this is permanent, never cleaned up
    socket.on('message_status_update', handleMessageStatusUpdate)

    // NO CLEANUP - listener stays active for entire app lifetime
    // This ensures status updates are received even after navigation
  }, [socket]) // Only depend on socket, not isConnected - attach when socket exists

  // Function to get message status
  const getMessageStatus = useCallback((messageId: string, whatsappMessageId?: string | null): MessageStatus | null => {
    // First try by messageId
    if (statuses.byMessageId[messageId]) {
      return statuses.byMessageId[messageId]
    }
    
    // Then try by whatsappMessageId
    if (whatsappMessageId && statuses.byWhatsappMessageId[whatsappMessageId]) {
      return statuses.byWhatsappMessageId[whatsappMessageId]
    }

    return null
  }, [statuses])

  // Function to manually update message status (for optimistic updates)
  const updateMessageStatus = useCallback((messageId: string, status: MessageStatus, whatsappMessageId?: string | null) => {
    setStatuses(prev => {
      const updated = { ...prev }
      
      if (messageId) {
        updated.byMessageId[messageId] = status
      }

      if (whatsappMessageId) {
        updated.byWhatsappMessageId[whatsappMessageId] = status
      }

      return updated
    })
  }, [])

  // Function to clear all statuses (for cleanup)
  const clearStatuses = useCallback(() => {
    setStatuses({
      byMessageId: {},
      byWhatsappMessageId: {}
    })
  }, [])

  const value: MessageStatusContextType = {
    getMessageStatus,
    updateMessageStatus,
    clearStatuses
  }

  return (
    <MessageStatusContext.Provider value={value}>
      {children}
    </MessageStatusContext.Provider>
  )
}

export function useMessageStatus() {
  const context = useContext(MessageStatusContext)
  if (context === undefined) {
    // Return default values if used outside provider
    return {
      getMessageStatus: () => null,
      updateMessageStatus: () => {},
      clearStatuses: () => {}
    }
  }
  return context
}

